import './GooglePlacesAutocomplete.scss'

import {countries} from 'countries-list'
import Autocomplete from 'react-google-autocomplete'

import {GOOGLE_MAPS_API_KEY} from '@/constants/config'

interface GoogleAddressComponent {
  long_name: string
  short_name: string
  types: string[]
}

interface GooglePlace {
  address_components?: GoogleAddressComponent[]
  formatted_address?: string
}

interface Props {
  defaultValue?: string
  onChange: (updates: {
    line1?: string
    country?: string
    state?: string
    city?: string
    postalCode?: string
  }) => void
  onBlur?: () => void
}

const getCountryCode = (fullName: string): string => {
  for (const [countryCode, countryInfo] of Object.entries(countries)) {
    if (countryInfo.name.toLowerCase() === fullName.toLowerCase()) {
      return countryCode
    }
  }
  return fullName
}

// Helper function to extract address components
const getAddressComponent = (
  addressComponents: GoogleAddressComponent[],
  types: string[],
  useShortName = false,
): string => {
  const component = addressComponents.find(c => types.every(type => c.types.includes(type)))
  if (!component) {
    return ''
  }
  if (useShortName) {
    return component.short_name
  }
  return component.long_name
}

export default memo(function AddressAutocomplete({defaultValue = '', onChange, onBlur}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const ignoreBlurRef = useRef(false)
  const [hasSuggestions, setHasSuggestions] = useState(false)

  const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    onChange({line1: value})
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  }

  const handlePlaceSelected = (place: GooglePlace) => {
    if (!place.address_components) {
      return
    }

    ignoreBlurRef.current = true

    const components = place.address_components

    // Extract and format address line 1
    const streetNumber = getAddressComponent(components, ['street_number'])
    const route = getAddressComponent(components, ['route'])
    const line1 =
      streetNumber && route
        ? `${streetNumber} ${route}`.trim()
        : (place.formatted_address?.split(',')[0]?.trim() ?? '')

    // Update input value
    if (inputRef.current) {
      inputRef.current.value = line1
    }

    // Extract all address components at once
    const updates: Parameters<typeof onChange>[0] = {line1}

    // Extract country
    const countryName = getAddressComponent(components, ['country'])
    const countryCode = getCountryCode(countryName)
    if (countryCode) {
      updates.country = countryCode
    }

    // Extract state/province
    const stateShortName = getAddressComponent(components, ['administrative_area_level_1'], true)
    const stateLongName = getAddressComponent(components, ['administrative_area_level_1'])
    if (stateShortName) {
      updates.state = stateShortName
    } else if (stateLongName) {
      updates.state = stateLongName
    }

    // Extract city with fallbacks
    let cityValue = getAddressComponent(components, ['locality'])
    if (!cityValue && countryCode === 'GB') {
      cityValue = getAddressComponent(components, ['postal_town'])
    }
    if (!cityValue) {
      cityValue = getAddressComponent(components, ['sublocality'])
    }
    if (cityValue) {
      updates.city = cityValue
    }

    // Extract postal code
    const postalCode = getAddressComponent(components, ['postal_code'])
    if (postalCode) {
      updates.postalCode = postalCode
    }

    // Send all updates at once
    onChange(updates)  
  }

  return (
    <Autocomplete
      apiKey={GOOGLE_MAPS_API_KEY}
      ref={(node) => {
        inputRef.current = node
        if (defaultValue && node) {
          node.value = defaultValue
        }
      }}
      options={{
        types: ['address'],
        fields: ['address_components', 'formatted_address', 'name', 'geometry'],
        componentRestrictions: {country: ['us', 'gb', 'au', 'ca']},
      }}
      id='dialog-line1'
      name='line1'
      autoComplete='address-line1'
      aria-required='true'
      aria-autocomplete='list'
      aria-controls='google-places-suggestions'
      aria-expanded={hasSuggestions}
      role='combobox'
      placeholder='Input your address'
      defaultValue={defaultValue}
      onChange={handleManualInputChange}
      onKeyDown={handleKeyDown}
      onBlur={() => {
        if (ignoreBlurRef.current) {
          ignoreBlurRef.current = false
          if (onBlur) onBlur()
          return
        }

        if (inputRef.current) {
          const manualValue = inputRef.current.value
          if (manualValue && manualValue !== defaultValue) {
            onChange({line1: manualValue})
          }
        }

        if (onBlur) onBlur()
      }}
      onPlaceSelected={handlePlaceSelected}
      onPlacesChanged={() => {
        setHasSuggestions(true)
      }}
    />
  )
})
